// Third party
import React, { Component } from 'react';
import { withRouter, RouteComponentProps, Route, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import qs from 'qs';

// Components
import { Locked, Login, SSO, ForgotPassword, SetPassword } from '.';
import AuthFooter from './AuthFooter';
import SetNewUserInfo from './SetNewUserInfo';
import Auth2FA from './Auth2FA';
import Setup2FA from './Setup2FA';
import Auth2FABackup from './Auth2FABackup';
import { Typography } from '@capintel/component-lib/typography';

// Images
import { ReactComponent as CapintelLogo } from '../../../images/capintel-logo.svg?react';

// Actions
import { ReduxState } from '../../../redux/modules/types';
import { setLocale } from '../../../redux/modules/intl';
import { logIn, generateSSOUrl, forgotPassword, setPassword, prepareOAuth2, validateResetToken, LOGIN } from '../../../redux/modules/auth';
import { getChargebeePortalSession } from '../../../redux/modules/subscription';
import { loadComparison, getComparisonById, validateComparisonToken } from '../../../redux/modules/comparison';
import { setRateLimits } from '../../../redux/modules/rate-limits';
import { selectIsLoadingEvery } from '../../../redux/modules/ui/loaders';
import { showModal as show2FAModal, verifyOTP } from '../../../redux/modules/two-factor-auth';

// Utils
import notification from '../../../utils/notification';
import { formatMessage, FormattedMessage } from '../../../utils/i18n';
import ChargebeeUtils from '../../../utils/3rd-party/chargebee';
import ZendeskUtils from '../../../utils/3rd-party/zendesk';
import { NAV_PATHS, PersistenceAuthType } from '../../../utils/constants';
import { RateLimits } from '../../../redux/modules/types/rate-limits';
import SessionTimeoutModal from '../../components/modals/SessionTimeoutModal';
import { ResetTokenValidation } from '../../../redux/modules/types/auth';
import Dictionary from '@/types/dictionary';
import User from '@/types/user';
import Locale from '@/types/locale';
import Comparison from '@/types/comparison';
import DataProvider from '@/types/data-provider';
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';

type StateProps = {
  authenticated: boolean;
  loadingOAuth2: boolean;
  user?: User | null;
  locale: Locale;
  signingIn: boolean;
  loading: boolean;
};

type DispatchProps = {
  logIn: (email: string, password: string) => Promise<User>;
  generateSSOUrl: (domain: string) => Promise<{ url: string }>;
  forgotPassword: (email: string) => Promise<void>;
  setPassword: (password: string, token: string, hash: string, fname?: string, lname?: string) => Promise<User>;
  getChargebeePortalSession: () => Promise<any>;
  loadComparison: (comparison: Comparison) => Promise<Comparison>;
  getComparisonById: (id: string) => Promise<Comparison>;
  validateComparisonToken: (token: string) => Promise<string>;
  prepareOAuth2: (provider: DataProvider, from: any) => void;
  setRateLimits: (rates?: RateLimits) => void;
  setLocale: (locale: Locale) => void;
  validateResetToken: (token: string) => Promise<ResetTokenValidation>;
  verifyOTP: (rememberMe: boolean, otp?: string, backup?: string) => Promise<void>;
  show2FAModal: () => void;
};

type State = {
  token?: string;
  hash?: string;
  reset?: boolean;
  isNew?: boolean;
  redirect?: string;
  comparisonToken?: string;
  options?: string;
  tokenValidation?: ResetTokenValidation;
  delegateAccessToken?: string;
};

const mapStateToProps = (state: ReduxState) => ({
  authenticated: state.auth.authenticated,
  loadingOAuth2: (state.auth && state.auth.loadingOAuth2) || false,
  user: state.auth && state.auth.user,
  locale: state.intl && state.intl.locale,
  signingIn: selectIsLoadingEvery(state, [LOGIN.type]),
  loading: state.twoFactorAuth.loading || false,
});

type Props = RouteComponentProps<{}, {}, { from: string }> & StateProps & DispatchProps;

class Auth extends Component<Props, State> {
  readonly state: State = {};

  twoFactorAuthHelpUrl: string;

  constructor(props: Props) {
    super(props);
    const { user, locale } = props;
    let redirect = '';
    if (user) {
      const {
        'comparison-token': comparisonToken,
        'delegate-access-token': delegateAccessToken,
        locale,
      } = qs.parse(props.location.search, { ignoreQueryPrefix: true });
      if (comparisonToken) {
        redirect = `${NAV_PATHS.COMPARISON}?comparison-token=${comparisonToken}`;
      }

      if (delegateAccessToken != null) {
        redirect = `/?delegate-access-token=${delegateAccessToken}`;
      }

      if (locale) {
        this.props.setLocale(locale as Locale);
      }
    }

    this.twoFactorAuthHelpUrl = `${import.meta.env.REACT_APP_HELP_SITE_URL}/${locale.toLocaleLowerCase()}/articles/9315570206483`;
    this.state = { redirect };
  }

  componentDidMount() {
    const {
      location: { search },
    } = this.props;
    const {
      token,
      hash,
      reset,
      'is-new': isNew,
      'comparison-token': comparisonToken,
      'delegate-access-token': delegateAccessToken,
    } = qs.parse(search, { ignoreQueryPrefix: true }) as Dictionary<string>;
    this.setState({ token, hash, reset: !!reset, isNew: !!isNew, comparisonToken, delegateAccessToken });

    if (token != null) {
      this.props.validateResetToken(token).then(data => {
        this.setState({ tokenValidation: data });
      });
    }
  }

  onLogin = async (email: string, password: string) =>
    this.props.logIn(email, password).then(user => {
      this.props.setRateLimits(user.remaining);
      this.redirectHome();
    });

  onGenerateSSOUrl = async (domain: string) =>
    this.props.generateSSOUrl(domain).then(data => {
      window.location.href = data.url;
    });

  twoFactorConfirmation = async (rememberMe: boolean, otp?: string, backup?: string) => {
    await this.props.verifyOTP(rememberMe, otp, backup);
    this.redirectHome();
  };

  onForgotPassword = (email: string) =>
    this.props.forgotPassword(email).then(() => {
      notification.success(formatMessage('Auth.ForgotPassword.EmailSent'));
    });

  showInvalidLinkToast = () => {
    const { match } = this.props;

    notification.errorFromKey('INVALID_LINK', [
      {
        label: formatMessage('Auth.GenerateNewLink'),
        onClick: () => this.props.history.push(`${match.path}/forgot-password`),
      },
    ]);
  };

  onSetPassword = async (password: string, fname?: string, lname?: string) => {
    const { token, hash } = this.state;

    if (!token || !hash) {
      this.showInvalidLinkToast();
      return Promise.reject();
    }

    try {
      const user = await this.props.setPassword(password, token, hash, fname, lname);
      this.props.setRateLimits(user.remaining);
      this.redirectHome();
      if (!user.authUser.mfa?.active) {
        this.props.show2FAModal();
      }
    } catch (err) {
      if ((err as { data: any }).data?.errorKey === 'INVALID_LINK') {
        this.showInvalidLinkToast();
      } else {
        throw err;
      }
    }
  };

  redirectHome = () => {
    const { comparisonToken, delegateAccessToken } = this.state;
    if (comparisonToken) {
      this.setState({ redirect: `${NAV_PATHS.COMPARISON}?comparison-token=${comparisonToken}` });
    } else if (delegateAccessToken != null) {
      this.setState({ redirect: `/?delegate-access-token=${delegateAccessToken}` });
    } else {
      this.setState({ redirect: '/' });
    }
  };

  onOpenCustomerPortal = () => {
    this.props.getChargebeePortalSession().then(async (session: any) => {
      ChargebeeUtils.openPortal(session);
    });
  };

  getOAuth2Provider = (user: User) => {
    // Get only the first OAUTH2 provider for now
    const dataProvider = user.dataProviders.find(
      d => d.authType === PersistenceAuthType.OAUTH2 && (d.grantType == null || d.grantType === 'authorization_code'),
    );
    if (dataProvider && (!user.oAuth2Providers || user.oAuth2Providers.length === 0 || !user.oAuth2Providers.find(o => o.name === dataProvider?.name))) {
      return dataProvider;
    }
    return null;
  };

  onLocaleChange = (locale: Locale) => {
    if (locale === this.props.locale) {
      return;
    }

    this.props.setLocale(locale);
    ZendeskUtils.setLocale(locale);

    // Use setTimeout to make sure language has been changed before sending the notification.
    setTimeout(() => {
      notification.success(formatMessage('Settings.Preferences.LanguageChanged'));
    }, 0);
  };

  get2faHelpUrl = `${import.meta.env.REACT_APP_HELP_SITE_URL}/${this.props.locale.toLocaleLowerCase()}/articles/9315570206483`;

  renderLogin = (props: RouteComponentProps): JSX.Element => {
    const { signingIn } = this.props;
    return <Login {...props} signingIn={signingIn} onSubmit={this.onLogin} />;
  };

  renderSSO = (props: RouteComponentProps): JSX.Element => {
    const { signingIn } = this.props;
    return <SSO {...props} signingIn={signingIn} onSubmit={this.onGenerateSSOUrl} />;
  };

  render2fa = (props: RouteComponentProps): JSX.Element => {
    const { authenticated, user, loading } = this.props;

    if (authenticated && !user) {
      return <Auth2FA {...props} helpUrl={this.twoFactorAuthHelpUrl} loading={loading} onSubmit={this.twoFactorConfirmation} />;
    }
    return <Redirect to="/" />;
  };

  render2faBackup = (props: RouteComponentProps): JSX.Element => {
    const { authenticated, user, loading } = this.props;

    if (authenticated && !user) {
      return <Auth2FABackup {...props} helpUrl={this.twoFactorAuthHelpUrl} loading={loading} onSubmit={this.twoFactorConfirmation} />;
    }
    return <Redirect to="/" />;
  };

  render2faSetup = (props: RouteComponentProps): JSX.Element => {
    const { authenticated, user } = this.props;

    return authenticated && !user ? <Setup2FA /> : <Redirect to="/" />;
  };

  renderForgotPassword = (props: RouteComponentProps): JSX.Element => <ForgotPassword {...props} onSubmit={this.onForgotPassword} />;

  renderSetPassword = (props: RouteComponentProps): JSX.Element => {
    const { tokenValidation, reset, isNew, delegateAccessToken } = this.state;
    const { locale } = this.props;
    const { from } = this.props.location.state || { from: { pathname: '/' } };

    // we should never be here without a 'tokenValidation' object, we wait for it.
    if (tokenValidation == null) {
      return <></>;
    }

    // the user has a password and is adding a delegate access (we do not care about the set-password token)
    if (!tokenValidation.tokenValid) {
      if (tokenValidation.userHasPassword && delegateAccessToken != null) {
        return <Redirect to={from} />;
      }

      return (
        // make sure this does not forward the parameters.
        // we need to show a toast
        <Redirect to={`${NAV_PATHS.AUTH}/login`} />
      );
    }

    if (!tokenValidation.userHasPassword) {
      return <SetNewUserInfo onSubmit={this.onSetPassword} locale={locale} />;
    }

    // if user has password and the link has 'is-new', then this was not a password reset request
    // also, if a 'delegate-access-token' is present, it was not a reset password request
    if (isNew || delegateAccessToken == null) {
      return <SetPassword {...props} onSubmit={this.onSetPassword} reset={reset} />;
    }

    return <Redirect to={from} />;
  };

  render() {
    const { match, user, loadingOAuth2, locale } = this.props;
    const { redirect, hash, token, tokenValidation } = this.state;
    const { from } = this.props.location.state || { from: { pathname: '/' } };

    if (user && !loadingOAuth2) {
      const dataProvider = this.getOAuth2Provider(user);
      if (dataProvider) {
        this.props.prepareOAuth2(dataProvider, from);
        return <Redirect to={NAV_PATHS.OAUTH2} />;
      }
    }

    if (redirect) {
      return <Redirect to={redirect} />;
    }

    if (user && (!hash || !token)) {
      return <Redirect to={from} />;
    }

    if (token != null && tokenValidation == null) {
      return <></>;
    }

    return (
      <div className={`auth auth-${locale}`}>
        <div className="auth-header d-flex align-items-center">
          <div className="container">
            <a href={import.meta.env.REACT_APP_WEBSITE_HOST} aria-label="CapIntel">
              <CapintelLogo className="capintel-logo logo-large" />
            </a>

            {/* This link is commented since the page doesn't exist yet */}
            {/* <p>
              {formatMessage('Auth.Login.SignupLink')}
              <Link to="" tabIndex={-1} className="link link-create-account">
                {formatMessage('Auth.Login.Signup')}
              </Link>
            </p> */}
          </div>
        </div>

        <div className="auth-content-container">
          <div className="auth-content">
            <div className="left-section">
              <Typography as={ALLOWED_HTML_TAGS.H2} variant="header2Xl">
                <FormattedMessage id="Auth.Login.Title" />
              </Typography>
              <p className="sub-title">
                <FormattedMessage id="Auth.Login.SubTitle" />
              </p>
            </div>
            <div className="right-section">
              <Route path={`${match.path}/login`} render={this.renderLogin} />
              <Route path={`${match.path}/sso`} render={this.renderSSO} />
              <Route path={`${match.path}/locked`} component={Locked} />
              <Route exact path={`${match.path}/2fa`} render={this.render2fa} />
              <Route exact path={`${match.path}/2fa/backup`} render={this.render2faBackup} />
              <Route exact path={`${match.path}/2fa/setup`} render={this.render2faSetup} />
              <Route path={`${match.path}/forgot-password`} render={this.renderForgotPassword} />
              <Route path={`${match.path}/set-password`} render={this.renderSetPassword} />
            </div>
          </div>
        </div>

        <AuthFooter locale={locale} onLocaleChange={this.onLocaleChange} />

        <SessionTimeoutModal />
      </div>
    );
  }
}

export default withRouter(
  connect(mapStateToProps, {
    logIn,
    generateSSOUrl,
    forgotPassword,
    setPassword,
    getChargebeePortalSession,
    loadComparison,
    getComparisonById,
    validateComparisonToken,
    prepareOAuth2,
    setRateLimits,
    setLocale,
    validateResetToken,
    verifyOTP,
    show2FAModal,
  })(Auth),
);
